import React from 'react';

// Brighter color palette while maintaining Morandi-inspired aesthetic
const colors = {
  background: '#f8f5f2',
  primary: '#f5cdaa',
  secondary: '#e27d60',
  accent: '#85cdca',
  text: '#3a3238',
  highlight: '#e8a87c'
};

// Presentation components
const Presentation = () => {
  return (
    <div style={{ 
      fontFamily: 'system-ui, -apple-system, sans-serif', 
      backgroundColor: colors.background,
      color: colors.text,
      padding: '2rem',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    }}>
      <Slide>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ 
              fontSize: '3rem', 
              marginBottom: '1rem',
              fontWeight: '700'
            }}>IMAGO</h1>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '400',
              marginTop: 0
            }}>Visual Search Application</h2>
          </div>
          <div style={{
            width: '8rem',
            height: '8rem',
            borderRadius: '50%',
            background: colors.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '4rem',
              height: '4rem',
              borderRadius: '50%',
              background: colors.highlight,
            }} />
          </div>
        </div>
      </Slide>

      <Slide>
        <div style={{ 
          background: colors.primary, 
          padding: '2rem',
          borderRadius: '0.5rem'
        }}>
          <h2 style={{ margin: 0, fontWeight: '600' }}>Problem & Opportunity</h2>
        </div>
        <div style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontWeight: '600' }}>Problem</h3>
            <ul style={{ listStyleType: 'none', padding: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>• Friction between visual discovery and purchasing</li>
              <li style={{ marginBottom: '0.5rem' }}>• Text-based search inadequate for visual content</li>
              <li style={{ marginBottom: '0.5rem' }}>• Existing tools limited to specific ecosystems</li>
            </ul>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontWeight: '600' }}>Opportunity</h3>
            <ul style={{ listStyleType: 'none', padding: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>• Visual search market reaching $150B+ by 2032</li>
              <li style={{ marginBottom: '0.5rem' }}>• 62% of Millennials/Gen Z demand visual search</li>
              <li style={{ marginBottom: '0.5rem' }}>• E-commerce conversion potential (30% increase)</li>
              <li style={{ marginBottom: '0.5rem' }}>• Online travel market approaching $2T</li>
            </ul>
          </div>
        </div>
      </Slide>

      <Slide>
        <h2 style={{ fontWeight: '600', display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            width: '1rem', 
            height: '1rem', 
            background: colors.highlight, 
            marginRight: '1rem',
            display: 'inline-block'
          }}></div>
          Solution: Imago
        </h2>
        <div style={{ 
          display: 'flex', 
          marginTop: '2rem',
          gap: '2rem'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              background: colors.accent,
              borderRadius: '0.5rem',
              padding: '1.5rem',
              height: '100%'
            }}>
              <h3 style={{ fontWeight: '600', marginTop: 0 }}>Core Technology</h3>
              <p>AI-powered image recognition that identifies products and places from any visual content</p>
              <p style={{ fontWeight: '600' }}>Platform: Web + iOS/Android</p>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              background: colors.secondary,
              color: 'white',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              height: '100%'
            }}>
              <h3 style={{ fontWeight: '600', marginTop: 0 }}>User Experience</h3>
              <p>1. Upload image/video</p>
              <p>2. AI identifies objects</p>
              <p>3. View product/place details</p>
              <p>4. Direct links to purchase/book</p>
            </div>
          </div>
        </div>
      </Slide>

      <Slide>
        <h2 style={{ fontWeight: '600', marginBottom: '2rem' }}>Technical Architecture</h2>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem'
        }}>
          <div style={{ 
            background: colors.primary,
            padding: '1.5rem',
            borderRadius: '0.5rem'
          }}>
            <h3 style={{ fontWeight: '600', marginTop: 0 }}>AI Engine</h3>
            <p>Google Cloud Vision AI API</p>
            <p>Landmark recognition</p>
            <p>Product identification</p>
            <p>Future: Gemini API integration</p>
          </div>
          <div style={{ 
            background: colors.primary,
            padding: '1.5rem',
            borderRadius: '0.5rem'
          }}>
            <h3 style={{ fontWeight: '600', marginTop: 0 }}>Infrastructure</h3>
            <p>Google Cloud Platform (GCP)</p>
            <p>Kubernetes (K8s)</p>
            <p>Scalable architecture</p>
          </div>
          <div style={{ 
            background: colors.primary,
            padding: '1.5rem',
            borderRadius: '0.5rem'
          }}>
            <h3 style={{ fontWeight: '600', marginTop: 0 }}>Frontend</h3>
            <p>React (Web)</p>
            <p>React Native (Mobile)</p>
            <p>Minimalist, visual interface</p>
          </div>
          <div style={{ 
            background: colors.primary,
            padding: '1.5rem',
            borderRadius: '0.5rem'
          }}>
            <h3 style={{ fontWeight: '600', marginTop: 0 }}>Data</h3>
            <p>AI results</p>
            <p>Affiliate network APIs</p>
            <p>Retailer/travel partnerships</p>
          </div>
        </div>
      </Slide>

      <Slide>
        <h2 style={{ fontWeight: '600' }}>Business Model</h2>
        <div style={{ 
          display: 'flex', 
          marginTop: '2rem',
          gap: '2rem',
          height: '15rem'
        }}>
          <div style={{ 
            background: colors.highlight,
            flex: 2,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <h3 style={{ fontWeight: '600' }}>Primary Revenue</h3>
            <p style={{ fontSize: '1.5rem' }}>Affiliate Marketing</p>
            <p>Commissions from product purchases and travel bookings</p>
          </div>
          <div style={{ 
            flex: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{ 
              background: colors.secondary,
              color: 'white',
              flex: 1,
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <h4 style={{ margin: 0 }}>API Licensing (Future)</h4>
              <p style={{ margin: 0 }}>PaaS model for businesses</p>
            </div>
            <div style={{ 
              background: colors.secondary,
              color: 'white',
              flex: 1,
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <h4 style={{ margin: 0 }}>Premium Subscriptions (Future)</h4>
              <p style={{ margin: 0 }}>Enhanced features</p>
            </div>
            <div style={{ 
              background: colors.secondary,
              color: 'white',
              flex: 1,
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <h4 style={{ margin: 0 }}>Sponsored Placements (Future)</h4>
              <p style={{ margin: 0 }}>Promoted products in results</p>
            </div>
            <div style={{ 
              background: colors.secondary,
              color: 'white',
              flex: 1,
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <h4 style={{ margin: 0 }}>Data Insights (Future)</h4>
              <p style={{ margin: 0 }}>Anonymized trend reports</p>
            </div>
          </div>
        </div>
      </Slide>

      <Slide>
        <h2 style={{ fontWeight: '600' }}>Target Market & Differentiation</h2>
        <div style={{ 
          display: 'flex',
          gap: '2rem',
          marginTop: '2rem'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              background: colors.accent,
              borderRadius: '0.5rem',
              padding: '1.5rem',
              height: '100%'
            }}>
              <h3 style={{ fontWeight: '600', marginTop: 0 }}>Primary Market</h3>
              <ul style={{ listStyleType: 'none', padding: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>• Millennials & Gen Z</li>
                <li style={{ marginBottom: '0.5rem' }}>• Visually-oriented consumers</li>
                <li style={{ marginBottom: '0.5rem' }}>• Frequent online shoppers</li>
                <li style={{ marginBottom: '0.5rem' }}>• Travelers</li>
                <li style={{ marginBottom: '0.5rem' }}>• Social media users</li>
              </ul>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              background: colors.primary,
              borderRadius: '0.5rem',
              padding: '1.5rem',
              height: '100%'
            }}>
              <h3 style={{ fontWeight: '600', marginTop: 0 }}>Competitive Advantage</h3>
              <ul style={{ listStyleType: 'none', padding: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>• Integrates product shopping AND travel planning</li>
                <li style={{ marginBottom: '0.5rem' }}>• Platform neutrality vs. ecosystem-locked tools</li>
                <li style={{ marginBottom: '0.5rem' }}>• Dedicated, minimalist interface</li>
                <li style={{ marginBottom: '0.5rem' }}>• Video support & robust API (roadmap)</li>
              </ul>
            </div>
          </div>
        </div>
      </Slide>

      <Slide>
        <h2 style={{ fontWeight: '600' }}>Market Potential</h2>
        <div style={{ 
          display: 'flex', 
          marginTop: '2rem',
          gap: '2rem',
          height: '12rem',
          alignItems: 'stretch'
        }}>
          <div style={{ 
            background: colors.highlight,
            color: colors.text,
            flex: 1,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>$150B+</p>
            <p style={{ fontSize: '1.2rem' }}>Visual Search Market<br/>(by 2032)</p>
          </div>
          <div style={{ 
            background: colors.accent,
            flex: 1,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>62%</p>
            <p style={{ fontSize: '1.2rem' }}>Millennials/Gen Z<br/>Demand Visual Search</p>
          </div>
          <div style={{ 
            background: colors.secondary,
            color: 'white',
            flex: 1,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>30%</p>
            <p style={{ fontSize: '1.2rem' }}>E-commerce<br/>Revenue Increase</p>
          </div>
          <div style={{ 
            background: colors.primary,
            flex: 1,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>~$2T</p>
            <p style={{ fontSize: '1.2rem' }}>Online Travel Market<br/>(early 2030s)</p>
          </div>
        </div>
      </Slide>

      <Slide>
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '20rem',
          background: colors.secondary,
          borderRadius: '0.5rem',
          padding: '2rem',
          color: 'white',
          textAlign: 'center'
        }}>
          <div>
            <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '2rem' }}>IMAGO</h1>
            <p style={{ fontSize: '1.5rem' }}>Transforming Visual Discovery</p>
            <p style={{ fontSize: '1.2rem', marginTop: '1rem' }}>Bridging the gap between visual inspiration and action</p>
          </div>
        </div>
      </Slide>
    </div>
  );
};

// Slide component
const Slide = ({ children }) => {
  return (
    <div style={{ 
      marginBottom: '4rem',
      minHeight: '20rem'
    }}>
      {children}
    </div>
  );
};

export default Presentation;